import { Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { minPlanLabelFor } from "../lib/planGate.js";
import { useIsRoot } from "../lib/useIsRoot.js";

/**
 * Inline lock shown where a feature is gated behind a higher plan. Renders a
 * small "Professional" badge with an upgrade link. The backend is the
 * real authority — this is the pre-emptive UI cue so users don't hit a 403.
 *
 * Props:
 *   feature — key from FEATURE_MIN_PLAN, used to derive the required plan label
 *   label   — optional override text (defaults to "<Plan> plan")
 *   compact — if true, render just the badge (for tight spots like nav items)
 */
export default function UpgradePrompt({ feature, label, compact = false }) {
  const planLabel = minPlanLabelFor(feature) || "a higher";
  const text = label || `${planLabel} plan`;
  const isRoot = useIsRoot();

  if (compact) {
    return (
      <span
        title={`Available on the ${planLabel} plan`}
        className="inline-flex items-center gap-1 rounded bg-gray-700 px-1.5 py-0.5 text-[10px] font-medium text-gray-300"
      >
        <Lock className="h-3 w-3" />
        {planLabel}
      </span>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
      <span className="flex items-center gap-2 text-amber-800">
        <Lock className="h-4 w-4 shrink-0" />
        Available on the {text}
      </span>
      {isRoot && (
        <Link
          to="/billing"
          className="shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
        >
          Upgrade
        </Link>
      )}
    </div>
  );
}
